import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import {
  LayoutDashboard, FileText, Plus, LogOut, User, Menu, X, Bell, MessageCircle, Users, CalendarRange, Inbox, ChevronRight, Target, ListTodo, FileSpreadsheet
} from 'lucide-react';

const HIMT_LOGO = 'https://customer-assets.emergentagent.com/job_report-approval-flow/artifacts/034nkv97_HIMT%20LOGO-PNG.png';

export default function TopNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [taskCounts, setTaskCounts] = useState({ open: 0, due_soon: 0, overdue: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  useEffect(() => { setMobileOpen(false); setNotifOpen(false); }, [location.pathname]);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.getPendingNotifications();
      setPendingItems(data?.items || []);
    } catch {}
    try {
      const { data } = await api.getMyTaskCounts();
      setTaskCounts(data || { open: 0, due_soon: 0, overdue: 0 });
    } catch {}
  }, []);

  // Poll for pending every 30s
  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [fetchPending, location.pathname]);

  // Close dropdown on outside click (use `click` so button onClick fires first)
  useEffect(() => {
    const handler = (e) => {
      const inDesktop = desktopDropdownRef.current && desktopDropdownRef.current.contains(e.target);
      const inMobile = mobileDropdownRef.current && mobileDropdownRef.current.contains(e.target);
      if (!inDesktop && !inMobile) setNotifOpen(false);
    };
    if (notifOpen) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [notifOpen]);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate('/login');
  };

  const handleNotifClick = (item) => {
    setNotifOpen(false);
    navigate(item.link);
  };

  const totalUnread = pendingItems.length;

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/reports', icon: FileText, label: 'Reports' },
  ];

  if (['Faculty', 'CIC', 'AcademicHead', 'PrincipalSSV'].includes(user?.role) || user?.also_faculty) {
    navItems.push({ to: '/reports/new', icon: Plus, label: 'New Report' });
  }

  // Everyone can be assigned tasks — show their personal task list with due-soon/overdue badges.
  navItems.push({ to: '/my-tasks', icon: ListTodo, label: 'My Tasks' });

  // Timetable Manager (or Super Admin) can build & publish timetables.
  if (user?.role_label === 'TimeTableManager' || user?.role === 'SuperAdmin') {
    navItems.push({ to: '/timetable', icon: CalendarRange, label: 'Timetable' });
  }
  // Every staff member — irrespective of role/designation — can see their own published schedule.
  navItems.push({ to: '/my-timetable', icon: CalendarRange, label: 'My Timetable' });

  if (['CIC', 'AcademicHead', 'PrincipalSSV', 'CEO', 'SuperAdmin'].includes(user?.role)) {
    navItems.push({ to: '/weekly-summary', icon: CalendarRange, label: 'Weekly Summary' });
    navItems.push({ to: '/tasks-analytics', icon: Target, label: 'Tasks' });
  }

  // Weekly Faculty Allocation Report — Time Table Manager (generate) + Head/Principal/CEO/Super Admin (view/download).
  if (user?.role_label === 'TimeTableManager' || ['AcademicHead', 'PrincipalSSV', 'CEO', 'SuperAdmin'].includes(user?.role)) {
    navItems.push({ to: '/allocation-report', icon: FileSpreadsheet, label: 'Allocation Report' });
  }

  if (user?.role === 'SuperAdmin') {
    navItems.push({ to: '/users', icon: Users, label: 'Users' });
  }

  const roleLabels = {
    Faculty: 'Faculty',
    AdminStaff: 'Admin Staff',
    ITStaff: 'IT Staff',
    TimeTableManager: 'Time Table Manager',
    CIC: 'CIC',
    AcademicHead: 'Head',
    PrincipalSSV: 'Principal',
    SuperAdmin: 'Super Admin',
    CEO: 'CEO'
  };

  const NotifBadge = ({ className = '' }) => totalUnread > 0 ? (
    <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold px-1 ${className}`}>
      {totalUnread > 99 ? '99+' : totalUnread}
    </span>
  ) : null;

  const TaskBadge = ({ className = '' }) => {
    const n = taskCounts.overdue || taskCounts.due_soon;
    if (!n) return null;
    const isOverdue = taskCounts.overdue > 0;
    return (
      <span
        data-testid="my-tasks-badge"
        title={`${taskCounts.overdue} overdue · ${taskCounts.due_soon} due soon`}
        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-white text-[10px] font-bold px-1 ${isOverdue ? 'bg-red-500' : 'bg-amber-500'} ${className}`}
      >
        {n > 99 ? '99+' : n}
      </span>
    );
  };

  const NotifDropdown = () => (
    <div data-testid="notification-dropdown" className="absolute right-0 top-full mt-2 w-[360px] bg-white text-slate-800 rounded-lg shadow-2xl border border-slate-200 overflow-hidden z-50">
      <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Pending</p>
          <p className="text-sm font-bold text-slate-900">{totalUnread === 0 ? 'You\'re all caught up' : `${totalUnread} item(s) need your action`}</p>
        </div>
        <Inbox className="w-4 h-4 text-slate-400" />
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {pendingItems.length === 0 ? (
          <div data-testid="no-pending-items" className="px-4 py-8 text-center text-sm text-slate-400">No pending items.</div>
        ) : (
          pendingItems.map((it) => (
            <button
              key={`${it.type}-${it.id}`}
              data-testid={`notif-item-${it.id}`}
              onClick={() => handleNotifClick(it)}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-start gap-3 transition-colors"
            >
              <div className={`w-2 h-2 rounded-full mt-1.5 ${it.type === 'weekly_summary' ? 'bg-blue-500' : it.type === 'task' ? 'bg-orange-500' : 'bg-emerald-500'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{it.title}</p>
                <p className="text-xs text-slate-500 truncate">{it.subtitle}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300 mt-1.5" />
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <header data-testid="top-nav" className="bg-slate-900 text-white sticky top-0 z-50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex items-center h-14 gap-4 sm:gap-8">
        {/* Logo */}
        <div className="flex items-center gap-2.5 shrink-0">
          <img src={HIMT_LOGO} alt="HIMT" className="w-9 h-9 object-contain rounded" />
          <div className="leading-tight">
            <h1 className="text-sm font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>HIMT</h1>
            <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400">Report System</p>
          </div>
        </div>

        <div className="w-px h-7 bg-slate-700 hidden sm:block" />

        {/* Desktop Navigation */}
        <nav className="hidden sm:flex items-center gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to ||
              (item.to === '/reports' && location.pathname.startsWith('/reports/') && item.to === '/reports');
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors relative ${
                  isActive ? 'bg-white/15 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
                {item.to === '/reports' && <NotifBadge className="ml-1" />}
                {item.to === '/my-tasks' && <TaskBadge className="ml-1" />}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex-1" />

        {/* Notification Bell — Desktop with dropdown */}
        <div className="hidden sm:block relative" ref={desktopDropdownRef}>
          <button
            data-testid="notification-bell"
            onClick={() => setNotifOpen(o => !o)}
            className="flex items-center relative text-slate-400 hover:text-white p-1.5 hover:bg-white/5 rounded-md transition-colors"
            title={totalUnread > 0 ? `${totalUnread} pending item(s)` : 'No pending items'}
          >
            <MessageCircle className="w-4.5 h-4.5" />
            {totalUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {totalUnread > 9 ? '9+' : totalUnread}
              </span>
            )}
          </button>
          {notifOpen && <NotifDropdown />}
        </div>

        {/* Desktop User info + Logout */}
        <div className="hidden sm:flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-slate-300" />
            </div>
            <div className="leading-tight">
              <p data-testid="nav-user-name" className="text-sm font-semibold text-white">{user?.name}</p>
              <p data-testid="nav-user-role" className="text-[10px] text-slate-400 font-medium">{roleLabels[user?.role_label] || roleLabels[user?.role] || user?.role_label || user?.role}</p>
            </div>
          </div>
          <button data-testid="logout-button" onClick={handleLogout} className="text-slate-400 hover:text-white transition-colors p-1.5 hover:bg-white/5 rounded-md" title="Sign out">
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile: notification + hamburger */}
        <div className="sm:hidden flex items-center gap-2 relative" ref={mobileDropdownRef}>
          <button
            data-testid="mobile-notification-bell"
            onClick={() => setNotifOpen(o => !o)}
            className="relative text-slate-300 hover:text-white p-1.5 rounded-md hover:bg-white/5 transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            {totalUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {totalUnread > 9 ? '9+' : totalUnread}
              </span>
            )}
          </button>
          {notifOpen && <NotifDropdown />}
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)} className="text-slate-300 hover:text-white p-1.5 rounded-md hover:bg-white/5 transition-colors">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="sm:hidden bg-slate-800 border-t border-slate-700 animate-fade-in">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to ||
                (item.to === '/reports' && location.pathname.startsWith('/reports/') && item.to === '/reports');
              return (
                <NavLink
                  key={item.to} to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? 'bg-white/15 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                  {item.to === '/reports' && <NotifBadge className="ml-1" />}
                  {item.to === '/my-tasks' && <TaskBadge className="ml-1" />}
                </NavLink>
              );
            })}
          </div>
          <div className="border-t border-slate-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-slate-300" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-white">{user?.name}</p>
                <p className="text-[10px] text-slate-400 font-medium">{roleLabels[user?.role_label] || roleLabels[user?.role] || user?.role_label || user?.role}</p>
              </div>
            </div>
            <button data-testid="mobile-logout-button" onClick={handleLogout} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors">
              <LogOut className="w-4 h-4" /><span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
